import { useEffect, useState } from "react";
import { getBlob, ref } from "firebase/storage";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Images,
  Loader2,
  Landmark,
  MapPin,
} from "lucide-react";

import { storage } from "integrations/firebase/storage";
import { CardGrid } from "components/CardGrid";
import { SectionHeader } from "components/SectionHeader";
import { EmptyState, LoadingCards } from "components/states";
import { SECTION_TITLES } from "../../copy/section-titles";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";

import { mapsLocationUrl } from "./directions";
import { ownedFloorPlanPaths } from "./floor-plans";
import type { Venue } from "./schemas";
import { useVenues } from "./use-event-content";

// The calm brand fills, each with white ink, taken in turn by position so a
// venue keeps its colour as long as the list keeps its order. The position
// carries no meaning: no venue state (open, full, closed) is shown by colour.
const TILE_FILLS = [
  "bg-hb-blue-fill",
  "bg-hb-purple-fill",
  "bg-hb-warm-grey",
] as const;

/** The count line under the title. */
export function venuesLine(count: number): string {
  if (count === 0) return "Nothing has been added yet.";
  const noun = count === 1 ? "venue" : "venues";
  return `${count} ${noun}. Open the map to find your way.`;
}

function VenuesHeader({ count }: { count?: number }) {
  return (
    <SectionHeader
      kicker={SECTION_TITLES.venues.kicker}
      title={SECTION_TITLES.venues.title}
      subline={count === undefined ? undefined : venuesLine(count)}
      testId="venues-header"
    />
  );
}

export function VenuesSection({ eventId }: { eventId: string }) {
  const venues = useVenues(eventId);

  // The head stays up through every state, so the tab does not jump when the
  // venues arrive.
  if (venues.status === "loading")
    return (
      <div className="space-y-5">
        <VenuesHeader />
        <LoadingCards cards={2} />
      </div>
    );
  if (venues.status === "error")
    return (
      <div className="space-y-5">
        <VenuesHeader />
        <EmptyState icon={AlertCircle} title="Could not load venues">
          {venues.error}
        </EmptyState>
      </div>
    );
  if (venues.data.length === 0)
    return (
      <div className="space-y-5">
        <VenuesHeader count={0} />
        <EmptyState icon={MapPin} title="No venues yet">
          Venues will appear here once added.
        </EmptyState>
      </div>
    );

  return (
    <div className="space-y-5">
      <VenuesHeader count={venues.data.length} />
      {/* One column on a phone, two from `md`, as on the attendee cards. */}
      <CardGrid
        className="gap-2.5 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2"
        testId="venues-content"
      >
        {venues.data.map((venue, index) => (
          <VenueCard
            key={venue.id}
            eventId={eventId}
            venue={venue}
            fill={TILE_FILLS[index % TILE_FILLS.length]}
          />
        ))}
      </CardGrid>
    </div>
  );
}

function VenueCard({
  eventId,
  venue,
  fill,
}: {
  eventId: string;
  venue: Venue;
  fill: string;
}) {
  const mapUrl = venueMapUrl(venue);
  const floorPlans = ownedFloorPlanPaths(
    eventId,
    venue.id,
    venue.floorPlanImages ?? [],
  );
  return (
    <article
      className="min-w-0 space-y-4 rounded-[22px] border border-border bg-card p-4 text-card-foreground shadow-card wrap-anywhere"
      data-testid={`venue-card-${venue.id}`}
    >
      <div className="flex items-start gap-3.5">
        <span
          aria-hidden="true"
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${fill} text-white`}
          data-testid={`venue-tile-${venue.id}`}
        >
          <Landmark className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[1.3125rem] leading-[1.12] text-foreground">
            {venue.name}
          </h3>
          {venue.address && (
            <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
              <MapPin
                aria-hidden="true"
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              <span className="min-w-0">{venue.address}</span>
            </p>
          )}
          {venue.description && (
            <p className="mt-1.5 text-sm text-muted-foreground">
              {venue.description}
            </p>
          )}
        </div>
      </div>
      {(mapUrl || floorPlans.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {mapUrl && (
            <Button asChild variant="outline" size="sm" className="min-h-11">
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`venue-directions-${venue.id}`}
              >
                <MapPin className="mr-1.5 h-4 w-4" />
                View on map
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          )}
          {floorPlans.length > 0 && (
            <FloorPlanGallery
              venueName={venue.name}
              storagePaths={floorPlans}
            />
          )}
        </div>
      )}
    </article>
  );
}

function FloorPlanGallery({
  venueName,
  storagePaths,
}: {
  venueName: string;
  storagePaths: string[];
}) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [image, setImage] = useState<
    | { status: "idle" | "loading" }
    | { status: "success"; url: string }
    | { status: "error" }
  >({ status: "idle" });

  useEffect(() => {
    if (!open) return;
    let active = true;
    let objectUrl: string | null = null;
    setImage({ status: "loading" });
    void getBlob(ref(storage, storagePaths[index]))
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setImage({ status: "success", url: objectUrl });
      })
      .catch(() => {
        if (active) setImage({ status: "error" });
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [index, open, storagePaths]);

  const move = (offset: number) => {
    setIndex(
      (current) =>
        (current + offset + storagePaths.length) % storagePaths.length,
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setIndex(0);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="min-h-11"
          data-testid="open-floor-plans"
        >
          <Images className="mr-1.5 h-4 w-4" />
          {storagePaths.length} floor plan
          {storagePaths.length === 1 ? "" : "s"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] w-[min(40rem,calc(100vw-2rem))] max-w-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{venueName} floor plans</DialogTitle>
          <DialogDescription>
            Floor plan {index + 1} of {storagePaths.length}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-72 items-center justify-center overflow-hidden rounded-lg bg-muted">
          {image.status === "loading" && (
            <Loader2
              className="h-8 w-8 animate-spin text-muted-foreground"
              aria-label="Loading floor plan"
            />
          )}
          {image.status === "error" && (
            <p role="alert" className="px-6 text-sm text-destructive-text">
              Could not load this floor plan.
            </p>
          )}
          {image.status === "success" && (
            <img
              src={image.url}
              alt={`${venueName} floor plan ${index + 1}`}
              className="max-h-[65vh] w-full object-contain"
            />
          )}
        </div>
        {storagePaths.length > 1 && (
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => move(-1)}
              aria-label="Previous floor plan"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => move(1)}
              aria-label="Next floor plan"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function venueMapUrl(venue: Venue): string | null {
  return mapsLocationUrl(
    venue.location
      ? `${venue.location.latitude},${venue.location.longitude}`
      : (venue.address ?? ""),
  );
}
